import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';

const defaultSubcategory = {
  id: "",
  name: "",
  categoryId: "",
  description: "",
  active: true,
  addedDate: ""
};

export default function Subcategories() {
  const [subcategories, setSubcategories] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState<any>(null);
  const [subcategory, setSubcategory] = useState<any>(defaultSubcategory);
  const [saving, setSaving] = useState(false);
  const [subcategorySearchTerm, setSubcategorySearchTerm] = useState("");
  const [subcategoryCategoryFilter, setSubcategoryCategoryFilter] = useState("");
  const [subcategoryActiveFilter, setSubcategoryActiveFilter] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesSnap, subcategoriesSnap] = await Promise.all([
          getDocs(collection(db, "categories")),
          getDocs(collection(db, "subcategories"))
        ]);

        const categoriesData = categoriesSnap.docs.map(doc => ({
          ...doc.data(),
          id: doc.id
        }));
        setCategories(categoriesData);

        const subcategoriesData = subcategoriesSnap.docs.map(doc => ({
          ...doc.data(),
          id: doc.id
        }));
        setSubcategories(subcategoriesData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredSubcategories = subcategories.filter((sub: any) => {
    const matchesSearch = (sub.name || "").toLowerCase().includes(subcategorySearchTerm.toLowerCase()) ||
      (sub.description || "").toLowerCase().includes(subcategorySearchTerm.toLowerCase());
    const matchesActive = subcategoryActiveFilter === "" ||
      (subcategoryActiveFilter === "active" && sub.active === true) ||
      (subcategoryActiveFilter === "inactive" && sub.active === false);
    const matchesCategory = subcategoryCategoryFilter === "" || sub.categoryId === subcategoryCategoryFilter;
    return matchesSearch && matchesActive && matchesCategory;
  });

  const handleSubcategoryChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    setSubcategory((prev: any) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const openCreateModal = (subcategoryToEdit?: any) => {
    if (subcategoryToEdit) {
      setEditingSubcategory(subcategoryToEdit);
      setSubcategory(subcategoryToEdit);
    } else {
      setEditingSubcategory(null);
      setSubcategory(defaultSubcategory);
    }
    setShowCreateModal(true);
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setSaving(true);

    try {
      const subcategoryData = {
        ...subcategory,
        addedDate: subcategory.addedDate || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      const { id, ...subcategoryDataToSave } = subcategoryData;

      if (editingSubcategory) {
        await setDoc(
          doc(db, "subcategories", editingSubcategory.id),
          subcategoryDataToSave,
          { merge: true }
        );
        setSubcategories((prev: any[]) => 
          prev.map(s => s.id === editingSubcategory.id ? { ...s, ...subcategoryData } : s)
        );
        alert("Subcategory updated successfully!");
      } else {
        const subcategoryRef = await addDoc(collection(db, "subcategories"), subcategoryDataToSave);
        const newSubcategory = { ...subcategoryData, id: subcategoryRef.id };
        setSubcategories((prev: any) => [...prev, newSubcategory]);
        alert("Subcategory created successfully!");
      }

      setShowCreateModal(false);
      setSubcategory(defaultSubcategory);
      setEditingSubcategory(null);
    } catch (error) {
      console.error("Error saving subcategory:", error);
      alert("Error saving subcategory. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-8">Loading...</div>;
  }

  return (
    <div className="p-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Subcategories</h1>
        <button
          onClick={() => openCreateModal()}
          className="bg-[#00e3ff] text-black px-4 py-2 rounded hover:bg-[#00e3ff]/80"
        >
          + Add Subcategory
        </button>
      </div>

      {/* Filters */}
      <div className="mb-4 grid grid-cols-6 gap-2">
        <input
          type="text"
          className="p-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#00e3ff] col-span-2"
          placeholder="Search subcategories by name or description..."
          value={subcategorySearchTerm}
          onChange={(e) => setSubcategorySearchTerm(e.target.value)}
        />
        <select
          className="p-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#00e3ff]"
          value={subcategoryCategoryFilter}
          onChange={(e) => setSubcategoryCategoryFilter(e.target.value)}
        >
          <option value="">All Categories</option>
          {categories.map((cat: any) => (
            <option key={cat.id} value={cat.id}>{cat.name}</option>
          ))}
        </select>
        <select
          className="p-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#00e3ff]"
          value={subcategoryActiveFilter}
          onChange={(e) => setSubcategoryActiveFilter(e.target.value)}
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto border rounded-lg">
        <table className="min-w-full border">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2 border text-center">Name</th>
              <th className="p-2 border text-center">Category</th>
              <th className="p-2 border text-center">Description</th>
              <th className="p-2 border text-center">Active</th>
              <th className="p-2 border text-center">Added Date</th>
              <th className="p-2 border text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {filteredSubcategories.map((sub: any) => {
              const parentCategory = categories.find(cat => cat.id === sub.categoryId);
              return (
                <tr key={sub.id} className="text-center">
                  <td className="p-2 border">{sub.name}</td>
                  <td className="p-2 border">{parentCategory ? parentCategory.name : "Unknown"}</td>
                  <td className="p-2 border">{sub.description || "-"}</td>
                  <td className="p-2 border">{sub.active ? "Yes" : "No"}</td>
                  <td className="p-2 border">
                    {sub.addedDate ? new Date(sub.addedDate).toLocaleDateString() : "-"}
                  </td>
                  <td className="p-2 border">
                    <button
                      className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
                      onClick={() => openCreateModal(sub)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filteredSubcategories.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            No subcategories found.
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">
                {editingSubcategory ? "Edit Subcategory" : "Create Subcategory"}
              </h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setSubcategory(defaultSubcategory);
                  setEditingSubcategory(null);
                }}
                className="text-gray-500 hover:text-gray-700 text-2xl"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={subcategory.name}
                  onChange={handleSubcategoryChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Category *</label>
                <select
                  name="categoryId"
                  value={subcategory.categoryId}
                  onChange={handleSubcategoryChange}
                  required
                  className="w-full p-2 border border-gray-300 rounded"
                >
                  <option value="">Select a category</option>
                  {categories.map((cat: any) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Description</label>
                <textarea
                  name="description"
                  value={subcategory.description || ""}
                  onChange={handleSubcategoryChange}
                  rows={3}
                  className="w-full p-2 border border-gray-300 rounded"
                />
              </div>

              <div className="mb-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="active"
                    checked={subcategory.active}
                    onChange={handleSubcategoryChange}
                    className="mr-2"
                  />
                  <span>Active</span>
                </label>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setSubcategory(defaultSubcategory);
                    setEditingSubcategory(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-[#00e3ff] text-black rounded hover:bg-[#00e3ff]/80 disabled:opacity-50"
                >
                  {saving ? "Saving..." : editingSubcategory ? "Update" : "Create"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

